/**
 * Screen printing pricing calculator — <spp-calculator> custom element.
 */
class SppCalculator extends HTMLElement {
  static isEmptyValue(value) {
    return (
      value === '' ||
      value === '0' ||
      value === '0.00' ||
      value === '$' ||
      value === '$0' ||
      value === '$0.00'
    );
  }

  static numberWithCommas(x) {
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
  }

  /** Display currency as $1,234.56 (comma + two decimals) on all totals and summary fields. */
  static formatCurrencyResult(amount) {
    const value = Number.isFinite(amount) ? amount : 0;
    const parts = value.toFixed(2).split('.');
    parts[0] = SppCalculator.numberWithCommas(parts[0]);
    return '$' + parts.join('.');
  }

  static isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  }

  static formatNumber(value) {
    return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  static parseQuantity(value) {
    if (value === '' || value == null) {
      return 0;
    }
    const parsed = parseFloat(String(value).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /** Strip $ and commas before parsing (main only stripped $, which broke $1,230.00 → 1). */
  static parseAmount(value) {
    if (value === '' || value == null) {
      return 0;
    }
    const parsed = parseFloat(String(value).replace(/[$,]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  static sanitizeQuantityInput(input) {
    let value = input.value.replace(/[^0-9.]/g, '');
    const dotIndex = value.indexOf('.');

    if (dotIndex !== -1) {
      value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
    }

    if (input.value !== value) {
      const caret = input.selectionStart;
      input.value = value;

      if (caret != null) {
        const nextCaret = Math.min(caret, value.length);
        input.setSelectionRange(nextCaret, nextCaret);
      }
    }
  }

  constructor() {
    super();
    this._abortController = null;
    this._calculateTotals = this._calculateTotals.bind(this);
  }

  connectedCallback() {
    this._mount();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
  }

  _input(name) {
    return this.querySelector('input[name="' + name + '"]');
  }

  _setFieldValue(field, value) {
    if (!field) {
      return;
    }

    if (field.tagName === 'OUTPUT') {
      field.textContent = value;
    }

    field.value = value;
  }

  _mount() {
    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._bindTooltips(signal);
    this._bindInputs(signal);
    this.classList.add('is-mounted');
  }

  _bindTooltips(signal) {
    this.querySelectorAll('[data-tooltip-trigger]').forEach((trigger) => {
      const tooltip = trigger.querySelector('[data-tooltip]');
      if (!tooltip) {
        return;
      }

      trigger.addEventListener('mouseenter', () => tooltip.classList.remove('hidden'), { signal });
      trigger.addEventListener('mouseleave', () => tooltip.classList.add('hidden'), { signal });
      trigger.addEventListener('focus', () => tooltip.classList.remove('hidden'), { signal });
      trigger.addEventListener('blur', () => tooltip.classList.add('hidden'), { signal });
    });
  }

  _bindInputs(signal) {
    this.querySelectorAll('input').forEach((input) => {
      const onInputChange = () => {
        if (input.dataset.type === 'currency') {
          this._formatCurrency(input);
        } else if (input.dataset.type === 'quantity') {
          SppCalculator.sanitizeQuantityInput(input);
        }

        this._calculateTotals();
      };

      input.addEventListener('input', onInputChange, { signal });
      input.addEventListener('keyup', onInputChange, { signal });

      if (input.dataset.type === 'currency') {
        const onCurrencyBlur = () => {
          this._formatCurrency(input, 'blur');
          this._calculateTotals();
        };

        if (SppCalculator.isTouchDevice()) {
          input.addEventListener('mouseout', onCurrencyBlur, { signal });
        } else {
          input.addEventListener('blur', onCurrencyBlur, { signal });
        }
      }
    });
  }

  _formatCurrency(input, blur) {
    let inputValue = input.value;

    if (inputValue === '') {
      this._calculateTotals();
      return;
    }

    const originalLength = inputValue.length;
    let caretPos = input.selectionStart;

    if (inputValue.indexOf('.') >= 0) {
      const decimalPos = inputValue.indexOf('.');
      let leftSide = SppCalculator.formatNumber(inputValue.substring(0, decimalPos));
      let rightSide = SppCalculator.formatNumber(inputValue.substring(decimalPos));

      if (blur === 'blur') {
        rightSide += '00';
      }

      rightSide = rightSide.substring(0, 2);
      inputValue = '$' + leftSide + '.' + rightSide;
    } else {
      inputValue = '$' + SppCalculator.formatNumber(inputValue);

      if (blur === 'blur') {
        inputValue += '.00';
      }
    }

    input.value = inputValue;

    const updatedLength = inputValue.length;
    caretPos = updatedLength - originalLength + caretPos;
    input.setSelectionRange(caretPos, caretPos);
  }

  _calculateTotals() {
    const inputs = this.querySelectorAll(
      'input.fillable:not(.total):not(.excluded):not([data-field="shipping-to-customer"]):not([data-field="garment-shipping-cost"])'
    );
    const totalOutputs = this.querySelectorAll('output.total');
    const totalGarmentQty = this.querySelector('[data-field="total-garment-qty"]');
    const shippingToCustomer = this.querySelector('[data-field="shipping-to-customer"]');

    const emptyInputs = Array.from(inputs).filter((input) => SppCalculator.isEmptyValue(input.value));

    this.classList.toggle('is-complete', emptyInputs.length === 0);

    if (emptyInputs.length > 0) {
      totalOutputs.forEach((output) => {
        this._setFieldValue(output, '');
      });
      this.querySelectorAll('output.summary[data-field]').forEach((output) => {
        this._setFieldValue(output, '');
      });
      return;
    }

    const garmentQty = SppCalculator.parseQuantity(totalGarmentQty?.value);
    let totalCostPerGarment = 0;
    let desiredProfitPerShirt = 0;

    // Row formulas match main calculator.js (with comma-safe currency parsing).
    const screenRowTotal =
      Math.round(
        ((SppCalculator.parseAmount(this._input('screen-cost')?.value) *
          SppCalculator.parseQuantity(this._input('qty-screens')?.value)) /
          garmentQty) *
          100
      ) / 100;
    totalCostPerGarment += screenRowTotal;
    this._setFieldValue(
      this.querySelector('output[data-total="first-total"]'),
      SppCalculator.formatCurrencyResult(screenRowTotal)
    );

    const locationRowTotal =
      Math.round(
        SppCalculator.parseAmount(this._input('locations-cost')?.value) *
          SppCalculator.parseQuantity(this._input('qty-locations')?.value) *
          100
      ) / 100;
    totalCostPerGarment += locationRowTotal;
    this._setFieldValue(
      this.querySelector('output[data-total="third-total"]'),
      SppCalculator.formatCurrencyResult(locationRowTotal)
    );

    const colorRowTotal =
      Math.round(
        SppCalculator.parseAmount(this._input('color-cost')?.value) *
          SppCalculator.parseQuantity(this._input('qty-color')?.value) *
          100
      ) / 100;
    totalCostPerGarment += colorRowTotal;
    this._setFieldValue(
      this.querySelector('output[data-total="fourth-total"]'),
      SppCalculator.formatCurrencyResult(colorRowTotal)
    );

    const garmentRowTotal =
      Math.round(
        (SppCalculator.parseAmount(this._input('garment-cost')?.value) +
          SppCalculator.parseAmount(this._input('garment-shipping-cost')?.value) / garmentQty) *
          100
      ) / 100;
    totalCostPerGarment += garmentRowTotal;
    this._setFieldValue(
      this.querySelector('output[data-total="fifth-total"]'),
      SppCalculator.formatCurrencyResult(garmentRowTotal)
    );

    desiredProfitPerShirt = SppCalculator.parseAmount(this._input('desired-profits')?.value);
    totalCostPerGarment += desiredProfitPerShirt;
    this._setFieldValue(
      this.querySelector('output[data-total="sixth-total"]'),
      SppCalculator.formatCurrencyResult(desiredProfitPerShirt)
    );

    const subtotal =
      Math.round(SppCalculator.parseQuantity(totalGarmentQty.value) * totalCostPerGarment * 100) / 100;

    this._setFieldValue(
      this.querySelector('[data-field="total-cost-per-garment"]'),
      SppCalculator.formatCurrencyResult(totalCostPerGarment)
    );
    this._setFieldValue(
      this.querySelector('[data-field="subtotal-order-cost"]'),
      SppCalculator.formatCurrencyResult(subtotal)
    );

    const totalOrderCost =
      SppCalculator.parseQuantity(totalGarmentQty.value) * totalCostPerGarment +
      SppCalculator.parseAmount(shippingToCustomer.value);

    this._setFieldValue(
      this.querySelector('[data-field="total-order-cost"]'),
      SppCalculator.formatCurrencyResult(totalOrderCost)
    );
    this._setFieldValue(
      this.querySelector('[data-field="profit-from-order"]'),
      SppCalculator.formatCurrencyResult(
        SppCalculator.parseQuantity(totalGarmentQty.value) * desiredProfitPerShirt
      )
    );
  }
}

if (!window.customElements.get('spp-calculator')) {
  window.customElements.define('spp-calculator', SppCalculator);
}
